import { useState } from "react";
import { CURRICULUM, type Module, type Lesson } from "@/data/curriculum";
import { type LucideIcon, ChevronDown, ChevronRight, ExternalLink, BookOpen, Clock, Video, FileText, Wrench, Search } from "lucide-react";

// ─── Helpers ───────────────────────────────────────────────────────────────────
const LEVEL_CLS: Record<string, string> = {
  Beginner:     "text-emerald-700 bg-emerald-50 border-emerald-200",
  Intermediate: "text-amber-700  bg-amber-50  border-amber-200",
  Advanced:     "text-rose-700   bg-rose-50   border-rose-200",
};
const TYPE_ICON: Record<string, LucideIcon> = {
  concept:      BookOpen,
  formula:      Wrench,
  exercise:     Wrench,
  "case-study": Search,
};
const TYPE_CLS: Record<string, string> = {
  concept:      "text-blue-600",
  formula:      "text-violet-600",
  exercise:     "text-amber-600",
  "case-study": "text-rose-600",
};
const RES_ICON: Record<string, LucideIcon> = {
  video:   Video,
  article: FileText,
  data:    Search,
  tool:    Wrench,
};

// ─── Lesson Panel ──────────────────────────────────────────────────────────────
function LessonPanel({ lesson }: { lesson: Lesson }) {
  const Icon = TYPE_ICON[lesson.type] ?? BookOpen;
  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden">
      {/* Lesson header */}
      <div className="p-5 border-b border-border bg-gray-50/60">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h4 className="font-semibold text-foreground text-sm leading-snug">{lesson.title}</h4>
          <div className="flex items-center gap-2 shrink-0">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock size={10} /> {lesson.duration}
            </span>
            <span className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded capitalize ${TYPE_CLS[lesson.type]}`}>
              <Icon size={10} /> {lesson.type.replace("-", " ")}
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">{lesson.intro}</p>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Paragraphs */}
        <div className="space-y-3">
          {lesson.body.map((para, i) => (
            <p key={i} className="text-xs text-foreground/80 leading-relaxed">{para}</p>
          ))}
        </div>

        {/* Formula box */}
        {lesson.formula && (
          <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
            <p className="text-[10px] font-semibold text-primary uppercase tracking-widest mb-1.5">{lesson.formula.label}</p>
            <code className="block text-sm font-mono text-foreground font-semibold">{lesson.formula.expression}</code>
            {lesson.formula.note && (
              <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">{lesson.formula.note}</p>
            )}
          </div>
        )}

        {/* Key points */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Key Takeaways</p>
          <ul className="space-y-1">
            {lesson.keyPoints.map((pt, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                {pt}
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        {lesson.resources.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Free Resources</p>
            <div className="flex flex-col gap-1.5">
              {lesson.resources.map((r, i) => {
                const RIcon = RES_ICON[r.type] ?? ExternalLink;
                const isInternal = r.url.startsWith("#");
                return isInternal ? (
                  <button key={i} onClick={() => document.getElementById(r.url.slice(1))?.scrollIntoView({ behavior: "smooth" })}
                    className="flex items-center gap-2 text-xs text-primary hover:text-primary/80 transition-colors text-left group">
                    <RIcon size={11} className="shrink-0" />
                    <span className="group-hover:underline">{r.label}</span>
                  </button>
                ) : (
                  <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-primary hover:text-primary/80 transition-colors group">
                    <RIcon size={11} className="shrink-0" />
                    <span className="group-hover:underline">{r.label}</span>
                    <ExternalLink size={9} className="shrink-0 opacity-50" />
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Module Card ───────────────────────────────────────────────────────────────
function ModuleCard({ mod }: { mod: Module }) {
  const [open, setOpen] = useState(false);
  const [activeLesson, setActiveLesson] = useState<string | null>(null);

  const totalMins = mod.lessons.reduce((acc, l) => {
    const n = parseInt(l.duration);
    return acc + (isNaN(n) ? 0 : n);
  }, 0);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      {/* Module header — always visible */}
      <button onClick={() => setOpen(o => !o)}
        className="w-full text-left px-6 py-5 flex items-start gap-4 hover:bg-gray-50/70 transition-colors group">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center">
          <span className="text-xs font-bold text-primary font-mono">{mod.n}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">{mod.title}</h3>
            <span className={`text-[9px] px-2 py-0.5 rounded-full border font-medium ${LEVEL_CLS[mod.level]}`}>{mod.level}</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">{mod.tagline}</p>
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><BookOpen size={10} /> {mod.lessons.length} lessons</span>
            <span className="flex items-center gap-1"><Clock size={10} /> {hours > 0 ? `${hours}h ` : ""}{mins > 0 ? `${mins}m` : ""}</span>
          </div>
        </div>
        <div className="shrink-0 text-muted-foreground mt-1">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {/* Lesson list */}
      {open && (
        <div className="border-t border-border">
          {/* Lesson index */}
          <div className="divide-y divide-border/50">
            {mod.lessons.map((lesson, idx) => {
              const Icon = TYPE_ICON[lesson.type] ?? BookOpen;
              const isActive = activeLesson === lesson.id;
              return (
                <div key={lesson.id}>
                  <button
                    onClick={() => setActiveLesson(isActive ? null : lesson.id)}
                    className={`w-full text-left flex items-center gap-3 px-6 py-3 transition-colors ${
                      isActive ? "bg-primary/5" : "hover:bg-gray-50/60"
                    }`}
                  >
                    <span className="text-[10px] font-mono text-muted-foreground w-5 shrink-0 text-right">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <Icon size={12} className={`shrink-0 ${TYPE_CLS[lesson.type]}`} />
                    <span className={`text-xs font-medium flex-1 ${isActive ? "text-primary" : "text-foreground"}`}>
                      {lesson.title}
                    </span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{lesson.duration}</span>
                    <ChevronDown size={12} className={`shrink-0 text-muted-foreground transition-transform ${isActive ? "rotate-180" : ""}`} />
                  </button>
                  {isActive && (
                    <div className="px-6 pb-5 pt-3 bg-gray-50/40 border-t border-border/50">
                      <LessonPanel lesson={lesson} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Export ───────────────────────────────────────────────────────────────
export function CurriculumSection() {
  const totalLessons = CURRICULUM.reduce((s, m) => s + m.lessons.length, 0);

  return (
    <section id="curriculum" className="space-y-6 scroll-mt-16">
      <div>
        <p className="text-xs text-primary font-medium uppercase tracking-widest mb-1">Curriculum</p>
        <h2 className="text-2xl font-bold">6 Modules · {totalLessons} Lessons</h2>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          From WACC fundamentals to live Asia-Pacific deal walkthroughs.
          Each lesson includes written explanations, key formulas, and links to free external resources — Investopedia, SEC filings, public company annual reports, and more.
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[10px]">
        {[
          { type: "concept",      label: "Concept",      icon: BookOpen },
          { type: "formula",      label: "Formula",      icon: Wrench   },
          { type: "case-study",   label: "Case Study",   icon: Search   },
        ].map(({ type, label, icon: Icon }) => (
          <span key={type} className="flex items-center gap-1.5 text-muted-foreground">
            <Icon size={11} className={TYPE_CLS[type]} /> {label}
          </span>
        ))}
        <span className="text-muted-foreground">· Click any lesson to expand full content and resources</span>
      </div>

      {/* Module cards */}
      <div className="flex flex-col gap-3">
        {CURRICULUM.map(mod => <ModuleCard key={mod.id} mod={mod} />)}
      </div>
    </section>
  );
}
